<template>
  <transition name="bg-modal">
    <div
      v-if="visible"
      class="bg-modal-overlay"
      role="presentation"
      @click.self="close"
    >
      <div
        class="bg-login-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bg-login-modal-title"
        @keydown.esc.prevent="close"
      >
        <header class="bg-login-modal__header">
          <div id="bg-login-modal-title" class="bg-login-modal__logo">
            <img v-if="logoUrl" :src="logoUrl" :alt="brandName" class="bg-login-modal__logo-img">
            <span v-else class="bg-login-modal__logo-fallback">{{ brandNameEn }}</span>
          </div>
          <button
            type="button"
            class="bg-login-modal__close"
            :aria-label="$t('common.close')"
            @click="close"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </header>

        <form class="bg-login-modal__body" @submit.prevent="submit">
          <label class="bg-login-modal__field">
            <span class="sr-only">{{ $t('auth.loginId') }}</span>
            <input
              ref="loginIdInput"
              v-model.trim="form.loginid"
              type="text"
              name="loginid"
              maxlength="20"
              autocomplete="username"
              :placeholder="$t('auth.loginIdPlaceholder')"
              :disabled="submitting"
            >
          </label>

          <label class="bg-login-modal__field bg-login-modal__field--password">
            <span class="sr-only">{{ $t('auth.password') }}</span>
            <input
              v-model="form.loginpwd"
              :type="showPassword ? 'text' : 'password'"
              name="loginpwd"
              maxlength="32"
              autocomplete="current-password"
              :placeholder="$t('auth.passwordPlaceholder')"
              :disabled="submitting"
            >
            <button
              type="button"
              class="bg-login-modal__toggle-pwd"
              :aria-label="showPassword ? $t('auth.passwordHide') : $t('auth.passwordShow')"
              :aria-pressed="showPassword ? 'true' : 'false'"
              tabindex="0"
              @click="showPassword = !showPassword"
            >
              <svg v-if="showPassword" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" fill="none" stroke="currentColor" stroke-width="1.8" />
                <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8" />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M3 3l18 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                <path d="M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-1.2M6.4 6.4C4.2 7.8 2.6 9.9 2 12s3.5 7 10 7c1.8 0 3.4-.4 4.8-1.1M14.1 9.9A3 3 0 0 0 12 9a3 3 0 0 0-2.1.9" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                <path d="M2 12s3.5-7 10-7c1.6 0 3 .3 4.2.8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
              </svg>
            </button>
          </label>

          <p v-if="errorMessage" class="bg-login-modal__error" role="alert">{{ errorMessage }}</p>

          <button type="submit" class="bg-login-modal__submit" :disabled="submitting">
            {{ submitting ? $t('auth.loginSubmitting') : $t('auth.loginSubmit') }}
          </button>
        </form>

        <footer class="bg-login-modal__footer">
          <p>{{ $t('auth.signupPrompt') }}</p>
          <button type="button" class="bg-login-modal__signup-link" @click="goSignup">
            {{ $t('auth.signupLink') }}
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { BRAND_NAME_EN } from '@/data/blackGoldSite'
import { resolveSiteLogoUrl } from '@/utils/siteBrand'

export default {
  name: 'BlackGoldLoginModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      brandNameEn: BRAND_NAME_EN,
      showPassword: false,
      submitting: false,
      errorMessage: '',
      form: {
        loginid: '',
        loginpwd: ''
      }
    }
  },
  watch: {
    visible (open) {
      if (open) {
        this.errorMessage = ''
        this.showPassword = false
        this.$nextTick(() => {
          if (this.$refs.loginIdInput) {
            this.$refs.loginIdInput.focus()
          }
        })
      } else {
        this.resetForm()
      }
    }
  },
  computed: {
    logoUrl () {
      return resolveSiteLogoUrl(this.$store.state.siteInfo)
    },
    brandName () {
      return this.$t('brand.name')
    }
  },
  methods: {
    close () {
      if (this.submitting) return
      this.$emit('close')
    },
    goSignup () {
      if (this.submitting) return
      this.$emit('signup')
    },
    resetForm () {
      this.form.loginid = ''
      this.form.loginpwd = ''
      this.errorMessage = ''
      this.showPassword = false
      this.submitting = false
    },
    submit () {
      if (this.submitting) return
      if (!this.form.loginid || !this.form.loginpwd) {
        this.errorMessage = this.$t('auth.loginEmptyError')
        return
      }
      this.errorMessage = ''
      this.submitting = true
      this.$emit('submit', { ...this.form })
    },
    setSubmitting (value) {
      this.submitting = value
    },
    setError (message) {
      this.errorMessage = message || ''
      this.submitting = false
    },
    finishSuccess () {
      this.submitting = false
      this.resetForm()
    }
  }
}
</script>
